import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable, Subject } from 'rxjs';
import { ApproveDraftsResponse, DraftActionType, RejectDraftsResponse, RemoveDraftResponse } from '../interfaces/apis/customer-drafts';

/**
 * Service for customer-related operations.
 */
@Injectable({
    providedIn: 'root',
})
export class CustomerService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = environment.apiBaseUrl;

    /** Subject to trigger draft table refresh across components */
    private readonly draftTableRefreshSubject = new Subject<DraftActionType | 'all'>();

    /** Observable that components can subscribe to for refresh events */
    readonly draftTableRefresh$ = this.draftTableRefreshSubject.asObservable();

    /**
     * Triggers a refresh of draft tables.
     * @param actionType - The specific draft type to refresh, or 'all' to refresh all
     */
    triggerDraftTableRefresh(actionType: DraftActionType | 'all' = 'all'): void {
        this.draftTableRefreshSubject.next(actionType);
    }

    /**
     * Adds customers to the deletion draft.
     * @param customerIds - Array of customer IDs to add to delete draft
     */
    addToDeleteDraft(customerIds: string[]): Observable<unknown> {
        return this.http.post(`${this.baseUrl}/customer-drafts/deletion-drafts`, {
            customerIds,
        });
    }

    /**
     * Rejects drafts from a customer draft.
     * PUT /api-v3/customer-drafts/reject-drafts/{customerDraftId}
     * @param customerDraftId - The ID of the customer draft
     * @param rejectedDraftIds - Array of draft IDs to reject
     */
    rejectDrafts(customerDraftId: string, rejectedDraftIds: string[]): Observable<RejectDraftsResponse> {
        return this.http.put<RejectDraftsResponse>(
            `${this.baseUrl}/customer-drafts/reject-drafts/${customerDraftId}`,
            { rejectedDraftIds }
        );
    }

    /**
     * Approves creation drafts.
     * PUT /api-v3/customer-drafts/creation-drafts-approve/{customerDraftId}
     * @param customerDraftId - The ID of the customer draft
     * @param acceptedDraftIds - Array of draft IDs to approve
     */
    approveCreationDrafts(customerDraftId: string, acceptedDraftIds: string[]): Observable<ApproveDraftsResponse> {
        return this.http.put<ApproveDraftsResponse>(
            `${this.baseUrl}/customer-drafts/creation-drafts-approve/${customerDraftId}`,
            { acceptedDraftIds }
        );
    }

    /**
     * Approves editing drafts.
     * PUT /api-v3/customer-drafts/editing-drafts-approve/{customerDraftId}
     * @param customerDraftId - The ID of the customer draft
     * @param acceptedDraftIds - Array of draft IDs to approve
     */
    approveEditingDrafts(customerDraftId: string, acceptedDraftIds: string[]): Observable<ApproveDraftsResponse> {
        return this.http.put<ApproveDraftsResponse>(
            `${this.baseUrl}/customer-drafts/editing-drafts-approve/${customerDraftId}`,
            { acceptedDraftIds }
        );
    }

    /**
     * Approves deletion drafts.
     * PUT /api-v3/customer-drafts/deletion-drafts-approve/{customerDraftId}
     * @param customerDraftId - The ID of the customer draft
     * @param acceptedDraftIds - Array of draft IDs to approve
     */
    approveDeletionDrafts(customerDraftId: string, acceptedDraftIds: string[]): Observable<ApproveDraftsResponse> {
        return this.http.put<ApproveDraftsResponse>(
            `${this.baseUrl}/customer-drafts/deletion-drafts-approve/${customerDraftId}`,
            { acceptedDraftIds }
        );
    }

    /**
     * Approves drafts based on the action type.
     * @param actionType - The type of draft action (Creation, Editing, Deletion)
     * @param customerDraftId - The ID of the customer draft
     * @param acceptedDraftIds - Array of draft IDs to approve
     */
    approveDrafts(actionType: DraftActionType, customerDraftId: string, acceptedDraftIds: string[]): Observable<ApproveDraftsResponse> {
        const endpointMap: Record<DraftActionType, string> = {
            'Creation': 'creation-drafts-approve',
            'Editing': 'editing-drafts-approve',
            'Deletion': 'deletion-drafts-approve',
        };
        const endpoint = endpointMap[actionType];
        return this.http.put<ApproveDraftsResponse>(
            `${this.baseUrl}/customer-drafts/${endpoint}/${customerDraftId}`,
            { acceptedDraftIds }
        );
    }

    /**
     * Removes a specific draft from a customer draft (for non-supervisors).
     * DELETE /api-v3/customer-drafts/{customerDraftId}/drafts/{draftId}
     * @param customerDraftId - The ID of the customer draft
     * @param draftId - The ID of the draft to remove
     */
    removeDraft(customerDraftId: string, draftId: string): Observable<RemoveDraftResponse> {
        return this.http.delete<RemoveDraftResponse>(
            `${this.baseUrl}/customer-drafts/${customerDraftId}/drafts/${draftId}`,
            { body: {} }
        );
    }
}

